import React, { useRef, useMemo } from 'react';
import { useFrame } from '@react-three/fiber';
import * as THREE from 'three';
import { WORLD } from '../config/zones';

// Animated, fast-moving Amazon River bisecting the world along the X axis.
function River() {
  const matRef = useRef();
  const { riverHalfWidth, xMin, xMax } = WORLD;

  useFrame((state) => {
    if (matRef.current) {
      // Scroll the normal/offset to fake a fast current.
      matRef.current.map && (matRef.current.map.offset.x = state.clock.elapsedTime * 0.35);
    }
  });

  return (
    <group>
      {/* Water surface */}
      <mesh rotation={[-Math.PI / 2, 0, 0]} position={[0, 0.05, 0]} receiveShadow>
        <planeGeometry args={[xMax - xMin + 60, riverHalfWidth * 2, 120, 4]} />
        <meshStandardMaterial
          ref={matRef}
          color="#1c6b82"
          metalness={0.6}
          roughness={0.2}
          transparent
          opacity={0.92}
        />
      </mesh>
      {/* Riverbed glow line down the middle */}
      <mesh rotation={[-Math.PI / 2, 0, 0]} position={[0, 0.08, 0]}>
        <planeGeometry args={[xMax - xMin + 60, 1.2]} />
        <meshBasicMaterial color="#2ec5ff" transparent opacity={0.25} />
      </mesh>
    </group>
  );
}

// Animated water ripples using vertex displacement each frame.
function Ripples() {
  const ref = useRef();
  const geo = useMemo(() => new THREE.PlaneGeometry(WORLD.xMax - WORLD.xMin + 60, WORLD.riverHalfWidth * 2, 80, 8), []);
  const base = useMemo(() => geo.attributes.position.array.slice(), [geo]);

  useFrame((state) => {
    if (!ref.current) return;
    const t = state.clock.elapsedTime;
    const pos = ref.current.geometry.attributes.position;
    for (let i = 0; i < pos.count; i++) {
      const x = base[i * 3];
      const y = base[i * 3 + 1];
      pos.setZ(i, Math.sin(x * 0.06 + t * 2.2) * 0.4 + Math.cos(y * 0.2 + t * 1.5) * 0.25);
    }
    pos.needsUpdate = true;
  });

  return (
    <mesh ref={ref} geometry={geo} rotation={[-Math.PI / 2, 0, 0]} position={[0, 0.12, 0]}>
      <meshStandardMaterial color="#2a86a0" metalness={0.5} roughness={0.25} transparent opacity={0.55} wireframe={false} />
    </mesh>
  );
}

export default function Terrain() {
  const { xMin, xMax, zMin, zMax, riverHalfWidth } = WORLD;
  const width = xMax - xMin + 80;

  return (
    <group>
      {/* Left bank ground (Progressive) */}
      <mesh rotation={[-Math.PI / 2, 0, 0]} position={[0, 0, (zMin - riverHalfWidth) / 2 - 5]} receiveShadow>
        <planeGeometry args={[width, Math.abs(zMin) - riverHalfWidth + 60]} />
        <meshStandardMaterial color="#16321f" roughness={1} />
      </mesh>
      {/* Right bank ground (Traditional) */}
      <mesh rotation={[-Math.PI / 2, 0, 0]} position={[0, 0, (zMax + riverHalfWidth) / 2 + 5]} receiveShadow>
        <planeGeometry args={[width, Math.abs(zMax) - riverHalfWidth + 60]} />
        <meshStandardMaterial color="#1c2e18" roughness={1} />
      </mesh>

      <River />
      <Ripples />
    </group>
  );
}
